/**
 * Azioni "Fissa in alto", "Archivia", "Silenzia" di una riga della lista chat.
 * La chiave e' il chat_jid (WhatsApp) oppure 'meta:<id>' (Instagram/Messenger).
 */
import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { useWhatsAppChatMetaStore } from '@/lib/stores/whatsappChatMetaStore';
import { isArchived, isMuted, isPinned, muteUntilFor, type ChatLike, type MuteOption } from '@/lib/services/whatsapp/chatListRules';

const ERRORE = 'Non sono riuscito a salvare la modifica. Riprova tra poco.';

export function useChatRowPrefs(organizationId: string | null | undefined) {
  const chatMeta = useWhatsAppChatMetaStore((s) => s.chatMeta);
  const prefsAvailable = useWhatsAppChatMetaStore((s) => s.listPrefsAvailable);
  const setChatListPrefs = useWhatsAppChatMetaStore((s) => s.setChatListPrefs);

  const stateOf = useCallback((key: string, chat: ChatLike) => {
    const meta = chatMeta.get(key);
    return { pinned: isPinned(meta), archived: isArchived(meta, chat), muted: isMuted(meta) };
  }, [chatMeta]);

  const salva = useCallback(async (key: string, changes: Parameters<typeof setChatListPrefs>[1], okMsg?: string) => {
    if (!organizationId) return;
    const ok = await setChatListPrefs(key, changes, organizationId);
    if (!ok) toast.error(ERRORE);
    else if (okMsg) toast.success(okMsg);
  }, [organizationId, setChatListPrefs]);

  const togglePin = useCallback((key: string, chat: ChatLike) => {
    const { pinned } = stateOf(key, chat);
    void salva(key, { is_pinned: !pinned });
  }, [stateOf, salva]);

  const toggleArchive = useCallback((key: string, chat: ChatLike) => {
    const { archived } = stateOf(key, chat);
    void salva(
      key,
      archived ? { is_archived: false, archived_at: null } : { is_archived: true, archived_at: new Date().toISOString(), is_pinned: false },
      archived ? 'Chat tolta dall\'archivio' : 'Chat archiviata: la trovi in "Archiviate". Torna in lista se ti scrivono.',
    );
  }, [stateOf, salva]);

  const mute = useCallback((key: string, option: MuteOption | null) => {
    void salva(key, { muted_until: option ? muteUntilFor(option) : null }, option ? 'Chat silenziata' : 'Notifiche riattivate');
  }, [salva]);

  return { stateOf, togglePin, toggleArchive, mute, prefsAvailable };
}
